import { whatsappReviewDraft } from "../../supabase/functions/_shared/whatsappReviewHandoff";
import type { WhatsAppReviewStage } from "../../supabase/functions/_shared/whatsappAttachmentReview";
import { strictWhatsAppReviewIds } from "../../supabase/functions/_shared/whatsappAttachmentReview";
import type { MirrorAuthLease, MirrorAuthSessionScope } from "./mirrorAuthSession";

/** Read and stage only. No turn, provider request, approval or receipt is issued. */
export async function loadMirrorWhatsAppReview(options: {
  messageId: string;
  scope: MirrorAuthSessionScope;
  lease: MirrorAuthLease;
  isMounted: () => boolean;
  readMessage: (messageId: string, ownerId: string) => Promise<unknown>;
  readSession: (sessionId: string, ownerId: string) => Promise<unknown>;
  now?: () => number;
}): Promise<string | null> {
  const current = () => options.isMounted() && options.scope.isCurrent(options.lease);
  if (!current()) return null;
  try {
    const message = await options.readMessage(options.messageId, options.lease.ownerId);
    if (!current() || !message || typeof message !== "object" || Array.isArray(message)) return null;
    const sessionId = (message as Record<string, unknown>).session_id;
    if (typeof sessionId !== "string" || !/^[0-9a-f-]{36}$/iu.test(sessionId)) return null;
    const session = await options.readSession(sessionId, options.lease.ownerId);
    if (!current()) return null;
    return whatsappReviewDraft(message, session, {
      ownerId: options.lease.ownerId, messageId: options.messageId, now: options.now?.(),
    });
  } catch {
    return null;
  }
}

/** Redeem via the authenticated server; original lease survives every await. */
export async function loadMirrorWhatsAppReviewStage(options: {
  messageId: string; scope: MirrorAuthSessionScope; lease: MirrorAuthLease; isMounted: () => boolean;
  readStage: (messageId: string, accessToken: string) => Promise<unknown>; now?: () => number;
}): Promise<WhatsAppReviewStage | null> {
  const current = () => options.isMounted() && options.scope.isCurrent(options.lease);
  const now = options.now ?? Date.now;
  if (!current()) return null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const value = await Promise.race([
      options.readStage(options.messageId, options.lease.accessToken),
      new Promise<null>(resolve => { timer = setTimeout(() => resolve(null), 20_000); }),
    ]);
    if (!current() || !value || typeof value !== "object" || Array.isArray(value)) return null;
    const stage = value as WhatsAppReviewStage;
    if (stage.messageId !== options.messageId || stage.ownerId !== options.lease.ownerId ||
      typeof stage.text !== "string" || !stage.text.trim() || stage.text.length > 4000 ||
      !Number.isFinite(stage.expiresAt) || stage.expiresAt <= now() || stage.expiresAt > now() + 125_000 ||
      !Array.isArray(stage.attachments) || stage.attachments.length > 20) return null;
    if (stage.attachments.length && !strictWhatsAppReviewIds(stage.attachments.map(row => row.id))) return null;
    if (stage.attachments.some(row => !row || typeof row.fileName !== "string" || !row.fileName ||
      row.fileName.length > 512 || !Number.isSafeInteger(row.byteSize) || row.byteSize <= 0 ||
      !["image", "pdf", "sheet"].includes(row.kind))) return null;
    return current() && stage.expiresAt > now() ? stage : null;
  } catch { return null; }
  finally { if (timer) clearTimeout(timer); }
}
export type { WhatsAppReviewStage };
